import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ScanTypeButton, { ScanDepth } from '../../components/ScanTypeButton';
import { useMutation, useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { remediationQueries } from '../../lib/queries';
import { useAuth } from '../../context/AuthContext';
import { useTheme as useCustomTheme } from '../../context/ThemeContext';
import { T, sans, card, topBtn, linkBtn } from '../Vendors/VendorDetails';
import { VendorLogo } from '../VendorRequests/VendorRequests';
import {
  EVENT_LABEL, RemediationDetailPayload, RemediationRisk, RequestStepper, SEVERITY_RANK, SeverityDot, StatusPill, apiError, daysUntil,
  downloadCsv, fmtDate, fmtDateTime, palette, relative, scoreColor,
} from './remediationShared';

type NoteAction = 'complete' | 'reopen';
type RiskFilter = 'all' | 'open' | 'fixed';
type FeedMode = 'conversation' | 'all';
const REMINDERS = [{ value: '', label: 'No reminder' }, { value: '1', label: '1 day before' }, { value: '3', label: '3 days before' },
  { value: '7', label: '1 week before' }, { value: '14', label: '2 weeks before' }];
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** /remediation/:requestId -- the all-vendors Remediation page's view of one request. */
export default function RemediationDetail() {
  const { requestId = '' } = useParams<{ requestId: string }>();
  const navigate = useNavigate();
  const { mode } = useCustomTheme();
  return (
    <div style={{ background: palette(mode === 'dark').bg, minHeight: '100vh', padding: '20px 24px' }}>
      <RemediationRequestView requestId={requestId} onBack={() => navigate('/remediation')} onDeleted={() => navigate('/remediation', { replace: true })} />
    </div>
  );
}

/** One request: progress, the picked risks and whether scans confirm them fixed, the conversation and activity, contacts,
 *  and the review actions. Used full-page by /remediation/:id and, with `embedded`, inside a vendor's Remediation tab. */
export function RemediationRequestView({ requestId, embedded = false, backLabel = 'Remediation', onBack, onDeleted }: {
  requestId: string;
  embedded?: boolean;
  backLabel?: string;
  onBack: () => void;
  onDeleted: () => void;
}) {
  const { user } = useAuth();
  const { mode } = useCustomTheme();
  const isDark = mode === 'dark';
  const c = palette(isDark);
  const canAct = !!user && user.role !== 'viewer';
  const isAdmin = !!user && (user.role === 'admin' || user.role === 'super_admin');

  const detailQuery = useQuery(remediationQueries.detail(requestId));
  const data = detailQuery.data as RemediationDetailPayload | undefined;

  const [expanded, setExpanded] = useState<string | null>(null);
  const [riskFilter, setRiskFilter] = useState<RiskFilter>('all');
  const [feedMode, setFeedMode] = useState<FeedMode>('conversation');
  const [draft, setDraft] = useState('');
  const [noteAction, setNoteAction] = useState<NoteAction | null>(null);
  const [note, setNote] = useState('');
  const [editingDue, setEditingDue] = useState(false);
  const [dueDraft, setDueDraft] = useState('');
  const [reminderDraft, setReminderDraft] = useState('');
  const [addingContact, setAddingContact] = useState(false);
  const [contactEmail, setContactEmail] = useState('');
  const [contactName, setContactName] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [toast, setToast] = useState<{ text: string; tone: 'ok' | 'error' } | null>(null);
  const feedBox = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const showToast = (text: string, tone: 'ok' | 'error' = 'ok') => {
    setToast({ text, tone });
    window.setTimeout(() => setToast(null), 4500);
  };
  const invalidates = { invalidates: [['remediation']] };
  const action = useMutation({
    mutationFn: async ({ path, body }: { path: string; body?: any }) => (await api.post(`/v1/remediation/${requestId}/${path}`, body ?? {})).data,
    meta: invalidates,
    onError: err => showToast(apiError(err, 'That did not work.'), 'error'),
  });
  const patch = useMutation({
    mutationFn: async (body: any) => (await api.patch(`/v1/remediation/${requestId}`, body)).data,
    meta: invalidates,
    onSuccess: () => { setEditingDue(false); showToast('Saved.'); },
    onError: err => showToast(apiError(err, 'Could not save.'), 'error'),
  });
  const removeContact = useMutation({
    mutationFn: (recipientId: string) => api.delete(`/v1/remediation/${requestId}/recipients/${recipientId}`),
    meta: invalidates,
    onError: err => showToast(apiError(err, 'Could not remove the contact.'), 'error'),
  });
  const deleteRequest = useMutation({
    mutationFn: () => api.delete(`/v1/remediation/${requestId}`),
    meta: invalidates,
    onSuccess: () => onDeleted(),
    onError: err => showToast(apiError(err, 'Could not delete the request.'), 'error'),
  });
  const rescan = useMutation({
    mutationFn: ({ vendorId, depth }: { vendorId: string; depth: ScanDepth }) =>
      api.post(`/v1/vendors/${vendorId}/trigger-scan`, null, { params: { scan_type: depth } }),
    meta: { invalidates: [['scans'], ['vendors']] },
    onSuccess: () => showToast('Scan started. Fixed risks update here when it finishes.'),
    onError: err => showToast(apiError(err, 'Could not start a scan.'), 'error'),
  });

  // The conversation and the activity log as one chronological feed (the request's own message comes first).
  const feed = useMemo(() => {
    if (!data) return [];
    const r = data.request;
    const items: { id: string; at: string; kind: 'message' | 'event'; mine?: boolean; author?: string; body?: string; label?: string; actor?: string | null }[] = [];
    if (r.message) items.push({ id: 'request-message', at: r.created_at || '', kind: 'message', mine: true, author: r.created_by_name || 'You', body: r.message });
    data.messages.forEach(m => items.push({ id: m.id, at: m.created_at, kind: 'message', mine: m.author_type === 'tenant',
      author: `${m.author_name || m.author_email || 'Unknown'}${m.author_type === 'vendor' ? ` · ${r.vendor_name}` : ''}`, body: m.body }));
    if (feedMode === 'all') {
      data.events.forEach(e => {
        const base = EVENT_LABEL[e.kind] || e.kind;
        const label = e.note ? (e.kind === 'sent' || e.kind === 'invite_resent' || e.kind.startsWith('recipient') ? `${base} ${e.note}`
          : e.kind.startsWith('risk_') ? `${base}: ${e.note}` : `${base} (${e.note})`) : base;
        items.push({ id: e.id, at: e.created_at, kind: 'event', label, actor: e.actor });
      });
    }
    return items.sort((a, b) => a.at.localeCompare(b.at));
  }, [data, feedMode]);

  // Keep the newest item in view by scrolling the feed itself, never the page.
  useEffect(() => { const box = feedBox.current; if (box) box.scrollTop = box.scrollHeight; }, [feed.length]);

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => { if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuOpen]);

  if (detailQuery.isPending) {
    return <div style={{ fontFamily: sans, padding: 40, textAlign: 'center', color: c.muted, fontSize: 13 }}>Loading request…</div>;
  }
  if (!data) {
    const missing = (detailQuery.error as any)?.response?.status === 404;
    return (
      <div style={{ ...card(isDark), padding: 32, textAlign: 'center', maxWidth: 520, margin: '24px auto', fontFamily: sans }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: c.ink, marginBottom: 6 }}>{missing ? 'Request not found' : "Couldn't load this request"}</div>
        <div style={{ fontSize: 13, color: c.slate, marginBottom: 14 }}>{missing ? 'It may have been deleted.' : apiError(detailQuery.error, 'Something went wrong.')}</div>
        <button onClick={onBack} style={{ ...topBtn(false, isDark), margin: '0 auto' }}>Back to {backLabel}</button>
      </div>
    );
  }

  const { request: r, risks, recipients, messages } = data;
  const fixedCount = risks.filter(x => x.status === 'fixed').length;
  const openCount = risks.length - fixedCount;
  const allFixed = risks.length > 0 && fixedCount === risks.length;
  const busy = action.isPending;
  const isOpen = r.status === 'in_progress' || r.status === 'awaiting_review';
  const remindedToday = !!r.last_reminder_at && new Date(r.last_reminder_at).toDateString() === new Date().toDateString();
  const lastVendorMessage = [...messages].reverse().find(m => m.author_type === 'vendor');
  const shownRisks = risks
    .filter(x => riskFilter === 'all' || x.status === riskFilter)
    .sort((a, b) => (a.status === b.status ? 0 : a.status === 'open' ? -1 : 1) || (SEVERITY_RANK[a.severity || ''] ?? 9) - (SEVERITY_RANK[b.severity || ''] ?? 9));
  const shareUrl = embedded
    ? `${window.location.origin}/vendors/${r.vendor_id}?tab=remediation&request=${r.id}`
    : `${window.location.origin}/remediation/${r.id}`;

  const run = (path: string, body?: any, done?: string) => action.mutate({ path, body }, { onSuccess: () => done && showToast(done) });
  const submitNote = () => {
    if (!noteAction) return;
    run(noteAction, { note: note.trim() || null }, noteAction === 'complete' ? 'Marked completed. The vendor was emailed.' : 'Sent back to the vendor.');
    setNoteAction(null);
    setNote('');
  };
  const sendMessage = () => {
    const body = draft.trim();
    if (!body) return;
    action.mutate({ path: 'messages', body: { body } }, { onSuccess: () => setDraft('') });
  };
  const addContact = () => {
    const email = contactEmail.trim().toLowerCase();
    if (!EMAIL_RE.test(email)) { showToast('Enter a valid email address.', 'error'); return; }
    action.mutate({ path: 'recipients', body: { email, name: contactName.trim() || null } }, {
      onSuccess: () => { setAddingContact(false); setContactEmail(''); setContactName(''); showToast(`Invitation sent to ${email}.`); },
    });
  };
  const copyLink = async () => {
    setMenuOpen(false);
    try { await navigator.clipboard.writeText(shareUrl); showToast('Link copied.'); } catch { window.prompt('Copy this link', shareUrl); }
  };
  const exportRisks = () => {
    setMenuOpen(false);
    downloadCsv(`${r.reference}-risks.csv`, ['Reference', 'Request', 'Vendor', 'Risk', 'Category', 'Severity at request', 'Status', 'Severity now', 'Fixed on', 'Recommendation'],
      risks.map(x => [r.reference, r.title, r.vendor_name, x.name, x.category, x.severity, x.status === 'fixed' ? 'Fixed' : 'Open',
        x.current_severity ?? '', x.fixed_at ? fmtDate(x.fixed_at) : '', x.recommendation]));
  };

  /* ─── Styles ─── */

  const sectionTitle: React.CSSProperties = { fontSize: 13, fontWeight: 700, color: c.ink };
  const label: React.CSSProperties = { fontSize: 10, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: c.muted };
  const input: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: 12.5, fontFamily: sans, color: c.ink,
    background: c.surface, border: `1px solid ${c.line}`, borderRadius: 6, outline: 'none',
  };
  const chip = (active: boolean): React.CSSProperties => ({
    fontSize: 11.5, fontWeight: 600, padding: '4px 10px', borderRadius: 99, cursor: 'pointer', fontFamily: sans,
    border: `1px solid ${active ? c.ink : c.line}`, background: active ? c.ink : c.surface, color: active ? (isDark ? '#0d0d0d' : '#fff') : c.slate,
  });
  const detailRow = (name: string, value: React.ReactNode) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '8px 0', borderBottom: `1px solid ${c.rowLine}`, fontSize: 12.5 }}>
      <span style={{ color: c.slate, flexShrink: 0 }}>{name}</span>
      <span style={{ color: c.ink, textAlign: 'right', minWidth: 0 }}>{value}</span>
    </div>
  );
  const menuItem = (text: string, onClick: () => void, danger = false) => (
    <button key={text} role="menuitem" onClick={onClick} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', background: 'none',
      border: 'none', cursor: 'pointer', fontSize: 12.5, fontFamily: sans, color: danger ? c.danger : c.ink, borderRadius: 4 }}
      onMouseEnter={e => { e.currentTarget.style.background = c.surface2; }} onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}>{text}</button>
  );

  const dueLabel = () => {
    if (!r.due_date) return <span style={{ color: c.muted }}>None</span>;
    const left = daysUntil(r.due_date);
    const live = r.status === 'in_progress';
    return (
      <span style={{ color: live && left < 0 ? c.danger : live && left <= 7 ? c.amber : c.ink, fontWeight: live && left <= 7 ? 600 : 400 }}>
        {fmtDate(r.due_date)}{live ? (left < 0 ? ` · ${-left}d overdue` : left === 0 ? ' · today' : ` · ${left}d left`) : ''}
      </span>
    );
  };

  /* ─── Header actions ─── */

  const primaryActions = canAct && (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
      {r.status === 'in_progress' && (
        <>
          <button style={{ ...topBtn(false, isDark), opacity: remindedToday || !openCount ? 0.55 : 1 }} disabled={busy || remindedToday || !openCount}
            title={remindedToday ? 'A reminder already went out today' : !openCount ? 'Every risk is already fixed' : 'Email every contact now'}
            onClick={() => run('remind', undefined, 'Reminder sent to the vendor.')}>{remindedToday ? 'Reminded today' : 'Send reminder'}</button>
          <ScanTypeButton label="Rescan vendor" busy={rescan.isPending} onPick={depth => rescan.mutate({ vendorId: r.vendor_id, depth })} isDark={isDark} buttonStyle={topBtn(false, isDark)} />
          <button style={topBtn(allFixed, isDark)} disabled={busy} onClick={() => setNoteAction('complete')}>Mark completed</button>
        </>
      )}
      {r.status === 'awaiting_review' && (
        <>
          <button style={topBtn(false, isDark)} disabled={busy} onClick={() => setNoteAction('reopen')}>Send back</button>
          <button style={topBtn(true, isDark)} disabled={busy} onClick={() => setNoteAction('complete')}>Mark completed</button>
        </>
      )}
      {r.status === 'completed' && <button style={topBtn(false, isDark)} disabled={busy} onClick={() => setNoteAction('reopen')}>Reopen</button>}
      {r.status === 'archived' && <button style={topBtn(true, isDark)} disabled={busy} onClick={() => run('unarchive', undefined, 'Unarchived.')}>Unarchive</button>}
      <div ref={menuRef} style={{ position: 'relative' }}>
        <button aria-label="More actions" aria-haspopup="menu" aria-expanded={menuOpen} onClick={() => setMenuOpen(o => !o)}
          style={{ ...topBtn(false, isDark), padding: '7px 10px' }}>⋯</button>
        {menuOpen && (
          <div role="menu" style={{ position: 'absolute', right: 0, top: 'calc(100% + 6px)', width: 190, background: c.surface, border: `1px solid ${c.line}`,
            borderRadius: 8, padding: 4, zIndex: 20, boxShadow: isDark ? '0 8px 30px rgba(0,0,0,.6)' : '0 8px 24px rgba(15,23,42,.12)' }}>
            {menuItem('Copy link', copyLink)}
            {menuItem('Export risks (CSV)', exportRisks)}
            {r.status !== 'archived' && r.status !== 'awaiting_review' && menuItem('Archive', () => { setMenuOpen(false); run('archive', undefined, 'Archived.'); })}
            {isAdmin && menuItem('Delete request', () => { setMenuOpen(false); setConfirmDelete(true); }, true)}
          </div>
        )}
      </div>
    </div>
  );

  /* ─── Pieces ─── */

  const riskRow = (risk: RemediationRisk, index: number) => {
    const open = expanded === risk.id;
    return (
      <div key={risk.id} style={{ borderTop: index === 0 ? 'none' : `1px solid ${c.rowLine}` }}>
        <button onClick={() => setExpanded(open ? null : risk.id)} style={{
          display: 'grid', gridTemplateColumns: '92px minmax(0, 1fr) auto 14px', gap: 12, alignItems: 'center', width: '100%', padding: '11px 16px',
          background: open ? c.surface2 : 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: sans,
        }}>
          <SeverityDot severity={risk.severity} isDark={isDark} />
          <span style={{ minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: risk.status === 'fixed' ? c.slate : c.ink }}>{risk.name}</span>
            <span style={{ display: 'block', fontSize: 11, color: c.muted }}>{risk.category || 'Other'}</span>
          </span>
          {risk.status === 'fixed' ? (
            <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 99, color: c.green, background: isDark ? '#132a1b' : T.grnBg, whiteSpace: 'nowrap' }}>
              Fixed {fmtDate(risk.fixed_at)}
            </span>
          ) : (
            <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 99, color: c.slate, background: isDark ? '#1a1a1a' : '#F1F5F9', whiteSpace: 'nowrap' }}>
              Open{risk.current_severity && risk.current_severity !== risk.severity ? ` · now ${risk.current_severity}` : ''}
            </span>
          )}
          <span style={{ color: c.muted, fontSize: 12, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>›</span>
        </button>
        {open && (
          <div style={{ padding: '2px 16px 16px 120px', fontSize: 12.5, lineHeight: 1.6, color: c.ink, background: c.surface2 }}>
            <div style={label}>What was found</div>
            <div style={{ margin: '4px 0 12px' }}>{risk.description || 'No description recorded.'}</div>
            {risk.recommendation && (
              <>
                <div style={label}>Recommendation</div>
                <div style={{ margin: '6px 0 12px', padding: '9px 12px', borderRadius: 6, background: isDark ? '#0c1d33' : '#EFF6FF',
                  border: `1px solid ${isDark ? '#1e3a5f' : '#BFDBFE'}`, color: isDark ? '#93c5fd' : '#1E40AF' }}>{risk.recommendation}</div>
              </>
            )}
            {risk.evidence && (
              <>
                <div style={label}>Evidence at request time</div>
                <pre style={{ margin: '4px 0 0', padding: 10, borderRadius: 6, background: c.surface, border: `1px solid ${c.line}`, fontSize: 11.5, color: c.slate,
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 220, overflow: 'auto', fontFamily: sans }}>{risk.evidence}</pre>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  const stat = (name: string, value: React.ReactNode, sub: React.ReactNode, color: string) => (
    <div style={{ padding: '14px 16px', minWidth: 0 }}>
      <div style={label}>{name}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color, marginTop: 6, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 11, color: c.muted, marginTop: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub}</div>
    </div>
  );
  const delta = r.initial_score != null && r.current_score != null ? r.current_score - r.initial_score : null;

  return (
    <div style={{ fontFamily: sans, display: 'grid', gap: 16 }}>
      {/* Header */}
      <div style={{ ...card(isDark), padding: '14px 18px' }}>
        <button onClick={onBack} style={{ fontSize: 12, color: c.slate, background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: sans }}>← {backLabel}</button>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginTop: 10, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 340px', minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <StatusPill status={r.status} isDark={isDark} />
              <span style={{ fontSize: 11.5, color: c.muted }}>{r.reference}</span>
            </div>
            <h1 style={{ fontSize: 19, fontWeight: 700, color: c.ink, margin: '8px 0 0', wordBreak: 'break-word' }}>{r.title}</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: 12, color: c.slate, flexWrap: 'wrap' }}>
              <VendorLogo name={r.vendor_name || '?'} domain={r.vendor_domain || ''} size={20} isDark={isDark} />
              {embedded ? <span style={{ fontWeight: 600, color: c.ink }}>{r.vendor_name}</span>
                : <Link to={`/vendors/${r.vendor_id}?tab=remediation&request=${r.id}`} style={{ fontWeight: 600, color: c.ink, textDecoration: 'none' }}>{r.vendor_name}</Link>}
              <span>·</span>
              <span>Requested {fmtDate(r.created_at)}{r.created_by_name ? ` by ${r.created_by_name}` : ''}</span>
              <span>·</span>
              <span>Due {dueLabel()}</span>
            </div>
          </div>
          {primaryActions}
        </div>
        <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${c.line}`, maxWidth: 560 }}>
          <RequestStepper r={r} isDark={isDark} />
        </div>
      </div>

      {/* What needs the tenant's attention */}
      {r.status === 'awaiting_review' && (
        <div style={{ ...card(isDark), padding: '14px 18px', borderLeft: `3px solid ${c.amber}` }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: c.ink }}>Ready for your review</div>
          <div style={{ fontSize: 12.5, color: c.slate, marginTop: 4, lineHeight: 1.55 }}>
            {r.vendor_name} submitted this request{r.submitted_at ? ` on ${fmtDate(r.submitted_at)}` : ''}.{' '}
            {allFixed ? <span style={{ color: c.green, fontWeight: 600 }}>The latest scan confirms every risk fixed.</span>
              : <span>{openCount} of {risks.length} risk{risks.length === 1 ? ' is' : 's are'} still flagged by the latest scan — a rescan may be needed.</span>}
          </div>
          {lastVendorMessage && (
            <div style={{ marginTop: 10, padding: '9px 12px', borderRadius: 6, background: c.surface2, border: `1px solid ${c.line}`, fontSize: 12.5, color: c.ink, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
              <span style={{ fontSize: 11, color: c.muted }}>{lastVendorMessage.author_name || lastVendorMessage.author_email} · {relative(lastVendorMessage.created_at)}</span>
              <div style={{ marginTop: 3 }}>{lastVendorMessage.body}</div>
            </div>
          )}
        </div>
      )}
      {r.status === 'in_progress' && allFixed && (
        <div style={{ ...card(isDark), padding: '12px 18px', borderLeft: `3px solid ${c.green}`, fontSize: 12.5, color: c.ink }}>
          The latest scan confirms every risk in this request fixed. You can mark it completed now, or wait for {r.vendor_name} to submit it.
        </div>
      )}
      {r.status === 'in_progress' && r.overdue && !allFixed && (
        <div style={{ ...card(isDark), padding: '12px 18px', borderLeft: `3px solid ${c.danger}`, fontSize: 12.5, color: c.ink }}>
          This request is past its due date with {openCount} risk{openCount === 1 ? '' : 's'} still open. Send a reminder or agree a new date with the vendor.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 320px', gap: 16, alignItems: 'start' }} className={embedded ? 'rem-grid-embedded' : 'rem-grid'}>
        <style>{`@media (max-width: 1000px) { .rem-grid { grid-template-columns: minmax(0, 1fr) !important; } }
          @media (max-width: 1240px) { .rem-grid-embedded { grid-template-columns: minmax(0, 1fr) !important; } }`}</style>

        {/* Main column */}
        <div style={{ display: 'grid', gap: 16, minWidth: 0 }}>
          <div style={{ ...card(isDark), padding: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
            {stat('Risks fixed', `${fixedCount}/${risks.length}`, allFixed ? 'all confirmed by scans' : `${openCount} still open`, allFixed ? c.green : c.ink)}
            {stat('Score at request', r.initial_score ?? '—', fmtDate(r.created_at), scoreColor(r.initial_score, isDark))}
            {stat('Score now', r.current_score ?? '—', delta ? `${delta > 0 ? '+' : ''}${delta} since the request` : 'no change since the request', scoreColor(r.current_score, isDark))}
            {stat('Projected', r.projected_score ?? '—', 'once every risk is fixed', scoreColor(r.projected_score, isDark))}
          </div>

          <div style={{ ...card(isDark), padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: `1px solid ${c.line}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <div style={sectionTitle}>Risks</div>
                <div style={{ fontSize: 11.5, color: c.muted, marginTop: 2 }}>Fixed once a scan no longer flags it as critical, high or medium.</div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {([['all', 'All', risks.length], ['open', 'Open', openCount], ['fixed', 'Fixed', fixedCount]] as const).map(([key, text, n]) => (
                  <button key={key} style={chip(riskFilter === key)} onClick={() => setRiskFilter(key)}>{text} {n}</button>
                ))}
              </div>
            </div>
            {shownRisks.length ? shownRisks.map(riskRow) : (
              <div style={{ padding: 24, textAlign: 'center', fontSize: 12.5, color: c.muted }}>{riskFilter === 'fixed' ? 'Nothing fixed yet.' : 'Nothing open.'}</div>
            )}
          </div>

          <div style={{ ...card(isDark), padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: `1px solid ${c.line}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <div style={sectionTitle}>{feedMode === 'conversation' ? 'Conversation' : 'Activity'}</div>
                <div style={{ fontSize: 11.5, color: c.muted, marginTop: 2 }}>Messages are shared with {r.vendor_name}'s contacts in the vendor portal.</div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button style={chip(feedMode === 'conversation')} onClick={() => setFeedMode('conversation')}>Messages</button>
                <button style={chip(feedMode === 'all')} onClick={() => setFeedMode('all')}>All activity</button>
              </div>
            </div>
            <div ref={feedBox} style={{ padding: 16, display: 'grid', gap: 12, maxHeight: 480, overflowY: 'auto' }}>
              {feed.length === 0 && <div style={{ fontSize: 12.5, color: c.muted, textAlign: 'center', padding: 12 }}>No messages yet. Start the conversation below.</div>}
              {feed.map(item => item.kind === 'event' ? (
                <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 11.5, color: c.muted }}>
                  <span style={{ flex: 1, height: 1, background: c.line }} />
                  <span style={{ textAlign: 'center', maxWidth: '75%' }}>{item.label}{item.actor ? ` · ${item.actor}` : ''} · {fmtDateTime(item.at)}</span>
                  <span style={{ flex: 1, height: 1, background: c.line }} />
                </div>
              ) : (
                <div key={item.id} style={{ display: 'flex', justifyContent: item.mine ? 'flex-end' : 'flex-start' }}>
                  <div style={{ maxWidth: '80%' }}>
                    <div style={{ fontSize: 11, color: c.muted, marginBottom: 3, textAlign: item.mine ? 'right' : 'left' }}>{item.author} · {relative(item.at)}</div>
                    <div style={{ padding: '9px 12px', borderRadius: 10, fontSize: 12.5, lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: c.ink,
                      background: item.mine ? (isDark ? '#0f1e38' : T.blueBg) : c.surface2, border: `1px solid ${item.mine ? (isDark ? '#1e3a5f' : T.blueBdr) : c.line}` }}>
                      {item.body}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {canAct && r.status !== 'archived' && (
              <div style={{ padding: 12, borderTop: `1px solid ${c.line}`, display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                <textarea value={draft} onChange={e => setDraft(e.target.value)} rows={2} maxLength={5000} placeholder={`Message ${r.vendor_name}… (Ctrl+Enter to send)`}
                  onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) sendMessage(); }}
                  style={{ ...input, resize: 'vertical', lineHeight: 1.5 }} />
                <button style={{ ...topBtn(true, isDark), opacity: draft.trim() ? 1 : 0.5 }} disabled={!draft.trim() || busy} onClick={sendMessage}>Send</button>
              </div>
            )}
          </div>
        </div>

        {/* Side column */}
        <div style={{ display: 'grid', gap: 16, minWidth: 0 }}>
          <div style={card(isDark)}>
            <div style={{ ...sectionTitle, marginBottom: 4 }}>Details</div>
            {detailRow('Reference', r.reference)}
            {editingDue ? (
              <div style={{ padding: '8px 0', borderBottom: `1px solid ${c.rowLine}`, display: 'grid', gap: 8 }}>
                <span style={label}>Due date and reminder</span>
                <input type="date" value={dueDraft} onChange={e => setDueDraft(e.target.value)} style={input} />
                <select value={reminderDraft} onChange={e => setReminderDraft(e.target.value)} disabled={!dueDraft} style={{ ...input, cursor: 'pointer' }}>
                  {REMINDERS.map(x => <option key={x.value} value={x.value}>{x.label}</option>)}
                </select>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button style={topBtn(false, isDark)} onClick={() => setEditingDue(false)}>Cancel</button>
                  <button style={topBtn(true, isDark)} disabled={patch.isPending}
                    onClick={() => patch.mutate({ due_date: dueDraft || null, reminder_days: dueDraft && reminderDraft ? Number(reminderDraft) : null })}>Save</button>
                </div>
              </div>
            ) : detailRow('Due', <span>{dueLabel()}{canAct && isOpen && (
              <button style={{ ...linkBtn(isDark), marginLeft: 8 }} onClick={() => { setDueDraft(r.due_date || ''); setReminderDraft(r.reminder_days != null ? String(r.reminder_days) : ''); setEditingDue(true); }}>Edit</button>
            )}</span>)}
            {!editingDue && detailRow('Reminder', r.due_date && r.reminder_days != null ? (REMINDERS.find(x => x.value === String(r.reminder_days))?.label || `${r.reminder_days} days before`) : 'Off')}
            {r.last_reminder_at && detailRow('Last reminder', relative(r.last_reminder_at))}
            {detailRow('Requested by', r.created_by_name || '—')}
            {detailRow('Requested', fmtDate(r.created_at))}
            {r.submitted_at && detailRow('Submitted', fmtDate(r.submitted_at))}
            {r.completed_at && detailRow('Completed', fmtDate(r.completed_at))}
            {detailRow('Categories', r.categories.length ? r.categories.join(', ') : '—')}
          </div>

          {!embedded && (
            <div style={card(isDark)}>
              <div style={{ ...sectionTitle, marginBottom: 10 }}>Vendor</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <VendorLogo name={r.vendor_name || '?'} domain={r.vendor_domain || ''} size={36} isDark={isDark} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: c.ink }}>{r.vendor_name}</div>
                  <div style={{ fontSize: 11.5, color: c.muted }}>{r.vendor_domain}</div>
                </div>
                {r.vendor_score != null && (
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: scoreColor(r.vendor_score, isDark), lineHeight: 1 }}>{r.vendor_score}</div>
                    <div style={{ fontSize: 10, color: c.muted, marginTop: 3 }}>score</div>
                  </div>
                )}
              </div>
              <Link to={`/vendors/${r.vendor_id}?tab=remediation&request=${r.id}`} style={{ ...linkBtn(isDark), display: 'inline-block', marginTop: 12, textDecoration: 'none' }}>
                Open vendor →
              </Link>
            </div>
          )}

          <div style={card(isDark)}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <span style={sectionTitle}>Vendor contacts</span>
              {canAct && isOpen && !addingContact && <button style={linkBtn(isDark)} onClick={() => setAddingContact(true)}>+ Add</button>}
            </div>
            {recipients.map(x => {
              const initials = (x.name || x.email).split(/[\s@.]+/).filter(Boolean).slice(0, 2).map(p => p[0]?.toUpperCase()).join('');
              return (
                <div key={x.id} style={{ display: 'flex', gap: 10, padding: '9px 0', borderBottom: `1px solid ${c.rowLine}`, alignItems: 'flex-start' }}>
                  <span style={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 11, fontWeight: 700, background: isDark ? '#1a1a1a' : '#EEF2FF', color: isDark ? '#94a3b8' : '#4338CA' }}>{initials}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: c.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={x.email}>{x.name || x.email}</div>
                    {x.name && <div style={{ fontSize: 11, color: c.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{x.email}</div>}
                    <div style={{ fontSize: 11, color: x.last_seen_at ? c.green : c.muted, marginTop: 2 }}>
                      {x.last_seen_at ? `Opened ${relative(x.last_seen_at)}` : `Invited ${relative(x.invited_at)} · not opened yet`}
                    </div>
                    {canAct && isOpen && (
                      <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                        <button style={{ ...linkBtn(isDark), padding: 0 }} disabled={busy} onClick={() => run(`recipients/${x.id}/resend`, undefined, `New invitation sent to ${x.email}.`)}>Resend invite</button>
                        {recipients.length > 1 && <button style={{ ...linkBtn(isDark), padding: 0, color: c.muted }} onClick={() => removeContact.mutate(x.id)}>Remove</button>}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {addingContact && (
              <div style={{ display: 'grid', gap: 8, paddingTop: 10 }}>
                <input autoFocus placeholder="Email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} style={input} />
                <input placeholder="Name (optional)" value={contactName} onChange={e => setContactName(e.target.value)} style={input} />
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button style={topBtn(false, isDark)} onClick={() => setAddingContact(false)}>Cancel</button>
                  <button style={topBtn(true, isDark)} disabled={busy || !contactEmail.trim()} onClick={addContact}>Send invitation</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {noteAction && createPortal(
        <Modal isDark={isDark} title={noteAction === 'complete' ? 'Mark this request completed?' : r.status === 'completed' ? 'Reopen this request?' : 'Send this request back?'}
          onClose={() => setNoteAction(null)}>
          <div style={{ fontSize: 12.5, color: c.slate, margin: '6px 0 12px', lineHeight: 1.55 }}>
            {noteAction === 'complete'
              ? (allFixed ? 'The latest scan confirms every risk fixed.' : `${openCount} risk${openCount === 1 ? ' is' : 's are'} still flagged by the latest scan. You can still complete it, for example if you accept the vendor's explanation.`)
              : 'It goes back to In progress and the vendor is emailed. Say what still needs doing.'}
          </div>
          <textarea autoFocus value={note} onChange={e => setNote(e.target.value)} rows={3} maxLength={5000}
            placeholder={noteAction === 'complete' ? 'Optional message to the vendor' : 'What still needs to be fixed?'}
            style={{ ...input, resize: 'vertical', lineHeight: 1.5 }} />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
            <button style={topBtn(false, isDark)} onClick={() => setNoteAction(null)}>Cancel</button>
            <button style={topBtn(true, isDark)} disabled={busy} onClick={submitNote}>{noteAction === 'complete' ? 'Mark completed' : r.status === 'completed' ? 'Reopen' : 'Send back'}</button>
          </div>
        </Modal>,
        document.body,
      )}

      {confirmDelete && createPortal(
        <Modal isDark={isDark} title="Delete this request?" onClose={() => setConfirmDelete(false)}>
          <div style={{ fontSize: 12.5, color: c.slate, margin: '6px 0 14px', lineHeight: 1.55 }}>
            Its risks, conversation and history are removed for good, and the vendor no longer sees it. Archive it instead to keep the record.
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button style={topBtn(false, isDark)} onClick={() => setConfirmDelete(false)}>Cancel</button>
            <button style={{ ...topBtn(true, isDark), background: c.danger }} disabled={deleteRequest.isPending} onClick={() => deleteRequest.mutate()}>Delete</button>
          </div>
        </Modal>,
        document.body,
      )}

      {toast && createPortal(
        <div role="status" style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 1400, padding: '10px 14px', borderRadius: 8, fontSize: 12.5, fontFamily: sans,
          background: c.surface, color: c.ink, border: `1px solid ${c.line}`, boxShadow: isDark ? '0 8px 30px rgba(0,0,0,.8)' : '0 8px 24px rgba(15,23,42,.12)' }}>
          <span style={{ color: toast.tone === 'ok' ? T.grn : c.danger, fontWeight: 700, marginRight: 8 }}>{toast.tone === 'ok' ? '✓' : '!'}</span>{toast.text}
        </div>,
        document.body,
      )}
    </div>
  );
}

function Modal({ isDark, title, onClose, children }: { isDark: boolean; title: string; onClose: () => void; children: React.ReactNode }) {
  const c = palette(isDark);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 1300, background: 'rgba(15,23,42,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, fontFamily: sans }}>
      <div role="dialog" aria-label={title} style={{ background: c.surface, border: `1px solid ${c.line}`, borderRadius: 10, width: 480, maxWidth: '100%', padding: 20 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: c.ink }}>{title}</div>
        {children}
      </div>
    </div>
  );
}
